'use client';
import React from 'react';
import { History, CheckCircle, AlertCircle, RefreshCw, FileCheck } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


type Certificate = {
  id: string;
  certId: string;
  company: string;
  status: string;
};

interface AuditTrailModalProps {
  cert: Certificate;
  onClose: () => void;
}

const auditEvents: Record<string, Array<{ id: string; event: string; actor: string; date: string; type: string }>> = {
  'cert-001': [
    { id: 'audit-001-1', event: 'Certificate issued', actor: 'JAKIM Recognized Partner', date: '15 Jan 2026, 09:14', type: 'success' },
    { id: 'audit-001-2', event: 'QR hash generated and registered', actor: 'HalalCham System', date: '15 Jan 2026, 09:15', type: 'success' },
    { id: 'audit-001-3', event: 'Certificate verified by Gulf Retail Group', actor: 'Gulf Retail Group', date: '22 Feb 2026, 14:30', type: 'info' },
    { id: 'audit-001-4', event: 'Annual compliance review passed', actor: 'JAKIM Recognized Partner', date: '10 May 2026, 11:00', type: 'success' },
    { id: 'audit-001-5', event: 'Certificate verified by US investor', actor: 'Crescent Capital Partners', date: '08 Jul 2026, 16:22', type: 'info' },
  ],
  'cert-003': [
    { id: 'audit-003-1', event: 'Certificate application submitted', actor: 'Zhejiang Halal Ingredients Co.', date: '22 Mar 2026, 10:00', type: 'info' },
    { id: 'audit-003-2', event: 'Documents received for review', actor: 'China Halal Certification Partner', date: '25 Mar 2026, 14:00', type: 'info' },
    { id: 'audit-003-3', event: 'Additional documents requested', actor: 'HalalCham Compliance Team', date: '02 Apr 2026, 09:30', type: 'warning' },
    { id: 'audit-003-4', event: 'Documents resubmitted by applicant', actor: 'Zhejiang Halal Ingredients Co.', date: '10 Apr 2026, 15:45', type: 'info' },
    { id: 'audit-003-5', event: 'Technical review in progress', actor: 'China Halal Certification Partner', date: '20 Jun 2026, 11:20', type: 'info' },
  ],
};

const defaultAuditEvents = (certId: string) => [
  { id: `audit-default-1-${certId}`, event: 'Certificate issued', actor: 'Certification Body', date: '01 Jan 2026, 09:00', type: 'success' },
  { id: `audit-default-2-${certId}`, event: 'QR hash generated and registered', actor: 'HalalCham System', date: '01 Jan 2026, 09:01', type: 'success' },
  { id: `audit-default-3-${certId}`, event: 'Certificate verified by trade partner', actor: 'Trade Partner', date: '15 Mar 2026, 14:00', type: 'info' },
];

const eventIcons: Record<string, React.ComponentType<{ size?: number; style?: React.CSSProperties }>> = {
  success: CheckCircle,
  warning: AlertCircle,
  info: RefreshCw,
};

const eventColors: Record<string, string> = {
  success: 'var(--emerald-bright)',
  warning: 'var(--warning)',
  info: 'var(--info)',
};

export default function AuditTrailModal({ cert, onClose }: AuditTrailModalProps) {
  const events = auditEvents[cert.id] || defaultAuditEvents(cert.certId);

  return (
    <Modal open onClose={onClose} title="Certificate Audit Trail" size="md">
      <div className="space-y-5">
        <div
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <History size={18} style={{ color: 'var(--emerald-bright)' }} />
          <div>
            <p className="text-sm font-semibold">{cert.certId}</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{cert.company}</p>
          </div>
          <div className="ml-auto">
            <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(10,107,79,0.15)', color: 'var(--emerald-bright)' }}>
              {events.length} events
            </span>
          </div>
        </div>

        <div className="relative">
          <div
            className="absolute left-5 top-0 bottom-0 w-0.5"
            style={{ background: 'var(--border)' }}
          />
          <div className="space-y-1">
            {events.map((event, idx) => {
              const Icon = eventIcons[event.type] || FileCheck;
              const isLast = idx === events.length - 1;
              return (
                <div key={event.id} className="relative flex items-start gap-4 pl-2 pb-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10"
                    style={{
                      background: `${eventColors[event.type]}15`,
                      border: `2px solid ${eventColors[event.type]}`,
                    }}
                  >
                    <Icon size={13} style={{ color: eventColors[event.type] }} />
                  </div>
                  <div
                    className="flex-1 rounded-xl p-3"
                    style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                  >
                    <p className="text-sm font-medium">{event.event}</p>
                    <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>by {event.actor}</span>
                      <span className="text-xs font-mono-data" style={{ color: 'var(--muted-foreground)' }}>{event.date}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="flex items-center gap-2 p-3 rounded-xl text-xs"
          style={{ background: 'rgba(10,107,79,0.08)', border: '1px solid rgba(10,107,79,0.2)' }}
        >
          <CheckCircle size={14} style={{ color: 'var(--emerald-bright)' }} />
          <span style={{ color: 'var(--emerald-bright)' }}>Audit trail is complete and tamper-evident</span>
        </div>
      </div>
    </Modal>
  );
}